/**
 * Coupons List Block - Frontend View
 *
 * Displays user's available coupons from spend rules and promotion rules.
 * Shows appropriate state based on enrollment status.
 */
import React, { type FC, useState, useEffect } from 'react';
import { createRoot } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import { useEnrollment } from '../_shared/hooks/use-enrollment';
import { useCart, useCartActions } from '../_shared/hooks/use-cart';
import Button from '../_shared/button';
import { getTranslatedText } from '../_shared/utils/translated-text';
import { replaceStrings } from '../_shared/utils/replacers';
import { joinProgram } from '~/shared/queries';
import {
	LeatApiError,
	customApplyCoupon,
	findCartItemKeyByVariationId,
	removeAllCoupons,
	removeCartItem,
	removeCoupon,
} from '~/shared/queries';
import {
	Item,
	ItemContent,
	ItemMedia,
	ItemLabel,
	ItemMeta,
	ItemBadge,
	ItemActions,
	ItemDescription,
	ItemGridSkeleton,
	CouponCode,
} from '../_shared/components';
import './style.scss';

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	cardLabelLevel: number;
	buttonTextSignIn: string;
	buttonTextViewDetails: string;
	messageGuest: string;
	messageNotEnrolled: string;
	emptyStateMessage: string;
	emptyStateButtonTextGuest: string;
	emptyStateButtonTextNotEnrolled: string;
	showEnrollmentPrompt: boolean;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
	buttonStyle?: {
		color?: {
			text?: string;
			background?: string;
		};
	};
}

interface CouponRule {
	id: number;
	label: { value: string | Record<string, string> | null; default?: Record<string, string> };
	creditCost?: { value: number };
	discountValue?: { value: number | string };
	discountType?: { value: string };
	image?: { value: string };
	description?: { value: string | Record<string, string> | null; default?: Record<string, string> };
	status: { value: string };
	svg?: string;
}

interface Coupon {
	type: 'spend_rule' | 'promotion_rule';
	code: string;
	rule: CouponRule;
}

interface GetCouponsResponse {
	spend_rules_coupons: Coupon[];
	promotion_rules_coupons: Coupon[];
}

interface CouponsListViewProps {
	attributes: Attributes;
}

// ============================================================================
// State Components
// ============================================================================

const GuestState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	return (
		<div className="leat-coupons-list__empty-state">
			<p>{attributes.messageGuest}</p>
			<Button href={redirectUrl} variant="contained">
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const NotEnrolledState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			const userId = window.leat?.userId ?? null;
			if (!userId) {
				throw new Error('User ID not found');
			}

			await joinProgram(userId);
			window.location.reload();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to join program';
			setJoinError(message);
			setIsJoining(false);
		}
	};

	return (
		<div className="leat-coupons-list__empty-state">
			<p>{attributes.messageNotEnrolled}</p>
			{attributes.showEnrollmentPrompt && (
				<>
					<Button
						onClick={handleJoinProgram}
						disabled={isJoining}
						variant="contained"
					>
						{isJoining ? 'Joining...' : attributes.emptyStateButtonTextNotEnrolled}
					</Button>
					{joinError && <p className="leat-coupons-list__error">{joinError}</p>}
				</>
			)}
		</div>
	);
};

// ============================================================================
// Main Component
// ============================================================================

const CouponsListView: FC<CouponsListViewProps> = ({ attributes }) => {
	const { status, creditsCurrency, isLoading: enrollmentLoading } = useEnrollment();
	const [coupons, setCoupons] = useState<Coupon[]>([]);
	const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Fetch coupons when enrolled
	useEffect(() => {
		const fetchCoupons = async () => {
			// Only fetch for enrolled users
			if (enrollmentLoading) {
				return;
			}

			if (status === 'guest' || status === 'loggedInNotEnrolled') {
				setIsLoadingCoupons(false);
				return;
			}

			try {
				const response = await apiFetch<GetCouponsResponse>({
					path: '/leat/v1/coupons'
				});

				// Combine both coupon types and filter by published status
				const allCoupons = [
					...response.spend_rules_coupons,
					...response.promotion_rules_coupons,
				].filter(
					(coupon) =>
						coupon.rule.status.value === 'publish' &&
						(coupon.rule.label.value || coupon.rule.label.default)
				);

				setCoupons(allCoupons);
				setIsLoadingCoupons(false);
			} catch (err) {
				// Check if this is a "user not enrolled" error
				const isNotEnrolledError =
					err.status === 404 ||
					err.code === 'no_contact_uuid' ||
					err.data?.status === 404 ||
					(err.message && err.message.toLowerCase().includes('not found'));

				if (isNotEnrolledError) {
					console.log('[Coupons List] User not enrolled (404) - showing empty state');
					setCoupons([]);
				} else {
					console.error('[Coupons List] API Error:', err);
					setError(err.message || 'Failed to load coupons');
				}
				setIsLoadingCoupons(false);
			}
		};

		fetchCoupons();
	}, [status, enrollmentLoading]);

	// Show loading state
	if (isLoadingCoupons || enrollmentLoading) {
		return (
			<ItemGridSkeleton
				columns={attributes.columns}
				showMeta={true}
				ariaMessage={__('Loading coupons...', 'leat-crm')}
				className="leat-coupons-list__grid"
			/>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="leat-coupons-list__error">
				<p>{error}</p>
			</div>
		);
	}

	// Handle enrollment states
	if (status === 'guest') {
		return <GuestState attributes={attributes} />;
	}

	if (status === 'loggedInNotEnrolled') {
		return <NotEnrolledState attributes={attributes} />;
	}

	// Show empty state for enrolled users with no coupons
	if (coupons.length === 0 && status === 'enrolled') {
		return (
			<div className="leat-coupons-list__empty">
				<p>{attributes.emptyStateMessage}</p>
			</div>
		);
	}

	// Get button inline styles
	const buttonInlineStyles: React.CSSProperties = {};
	buttonInlineStyles.borderColor = 'transparent';

	if (attributes.buttonStyle?.color?.text) {
		buttonInlineStyles.color = attributes.buttonStyle.color.text;
	} else if (attributes.buttonTextColor) {
		buttonInlineStyles.color = `var(--wp--preset--color--${attributes.buttonTextColor})`;
	}
	if (attributes.buttonStyle?.color?.background) {
		buttonInlineStyles.backgroundColor = attributes.buttonStyle.color.background;
	} else if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = `var(--wp--preset--color--${attributes.buttonBackgroundColor})`;

	}

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	// Show coupons grid
	return (
		<div className="leat-coupons-list__grid" style={gridStyle}>
			{coupons.map((coupon, index) => (
				<CouponCard
					key={`${coupon.type}-${coupon.code}-${index}`}
					coupon={coupon}
					creditsCurrency={creditsCurrency ?? 'points'}
					labelLevel={attributes.cardLabelLevel}
					buttonTextViewDetails={attributes.buttonTextViewDetails}
					buttonInlineStyles={buttonInlineStyles}
					imageHeight={attributes.imageHeight}
					imageFit={attributes.imageFit}
				/>
			))}
		</div>
	);
};

// ============================================================================
// Coupon Card Component
// ============================================================================

const CouponCard: FC<{
	coupon: Coupon;
	creditsCurrency: string;
	labelLevel: number;
	buttonTextViewDetails: string;
	buttonInlineStyles?: React.CSSProperties;
	imageHeight: number;
	imageFit: 'contain' | 'cover' | 'fill';
}> = ({ coupon, creditsCurrency, labelLevel, buttonTextViewDetails, buttonInlineStyles, imageHeight, imageFit }) => {
	const [showCode, setShowCode] = useState(false);
	const [isApplying, setIsApplying] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Cart state management
	const { cart, isCouponApplied } = useCart();
	const { refetchCart } = useCartActions();

	// Check if coupon is applied
	const isApplied = isCouponApplied(coupon.code);
	const hasProducts = coupon.rule.selectedProducts?.value?.length > 0;

	// Error message parser
	function getErrorMessage(error: unknown): string {
		console.log('Parsing error:', error);

		// Handle LeatApiError
		if (error instanceof LeatApiError) {
			// The LeatApiError stores the message in multiple places
			// Priority: message > data (if string) > statusText
			if (error.message && error.message !== 'LeatApiError: unknown') {
				return error.message;
			}
			if (error.data && typeof error.data === 'string' && error.data !== 'Unknown error occurred') {
				return error.data;
			}
			if (error.statusText && error.statusText !== 'unknown') {
				return error.statusText;
			}
			return 'Error applying coupon';
		}

		// Handle standard Error objects
		if (error instanceof Error) {
			return error.message;
		}

		// Handle plain objects with message property
		if (error && typeof error === 'object' && 'message' in error) {
			return String((error as any).message);
		}

		return 'An unknown error occurred';
	}

	// Apply coupon handler
	async function handleApplyCoupon() {
		// Don't do anything if the coupon is already applied
		if (isApplied) {
			setError('This coupon is already applied to your cart');
			return;
		}

		setIsApplying(true);
		setError(null);

		try {
			// Check if cart has other coupons that might conflict
			if (cart?.coupons && cart.coupons.length > 0) {
				// If cart has no items but has coupons, remove all coupons first
				if (cart.items_count === 0) {
					await removeAllCoupons();
				} else {
					// If cart has items and coupons, and the coupon is not already applied,
					// try to remove conflicting coupons
					const hasConflicts = cart.coupons.some(c => c.code !== coupon.code);
					if (hasConflicts) {
						await removeAllCoupons();
					}
				}
			}

			// Apply the coupon (backend handles adding products automatically)
			await customApplyCoupon(coupon.code);

			// Refetch cart to update UI
			refetchCart();

			// Redirect to cart/checkout if cart has items
			const cartPage = window.leat_dashboard?.storePages?.cart?.permalink;
			const checkoutPage = window.leat_dashboard?.storePages?.checkout?.permalink;

			if (cart?.items_count !== 0) {
				if (cartPage) {
					window.location.href = cartPage;
				} else if (checkoutPage) {
					window.location.href = checkoutPage;
				}
			}
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setIsApplying(false);
		}
	}

	// Remove coupon handler
	async function handleRemoveCoupon() {
		setIsApplying(true);
		setError(null);

		try {
			// First check if this coupon has selected products
			if (hasProducts && coupon.rule.selectedProducts?.value) {
				// For each selected product in the coupon
				for (const productId of coupon.rule.selectedProducts.value) {
					// Find if this product is in the cart
					const numProductId = Number(productId);
					const cartItemKey = await findCartItemKeyByVariationId(numProductId);

					// If found in cart, remove it
					if (cartItemKey) {
						await removeCartItem(cartItemKey);
					}
				}
			}

			// Then remove the coupon
			await removeCoupon(coupon.code);

			// Refetch cart to update the UI
			refetchCart();
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setIsApplying(false);
		}
	}

	// Get translated label and description (fall back to default if value is null)
	const labelValue = coupon.rule.label.value ?? coupon.rule.label.default;
	const label = typeof labelValue === 'string' ? labelValue : getTranslatedText(labelValue);

	const descriptionValue = coupon.rule.description?.value ?? coupon.rule.description?.default;
	const description = descriptionValue
		? typeof descriptionValue === 'string'
			? descriptionValue
			: getTranslatedText(descriptionValue)
		: null;

	// Prepare replacement variables
	const creditCost = coupon.rule.creditCost?.value;
	const discount = coupon.rule.discountValue?.value;
	const replacementVariables = {
		'{{credits}}': creditCost?.toString() || '0',
		'{{credits_currency}}': creditsCurrency,
		'{{discount}}': discount?.toString() || '0',
		'{{coupon}}': coupon.code,
	};

	// Replace variables in label and description
	const displayLabel = replaceStrings(label, [replacementVariables]);
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;

	// Create dynamic heading tag
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	return (
		<Item
			variant="default"
			style={{
				'--leat-image-height': `${imageHeight}px`,
				'--leat-image-fit': imageFit,
			} as React.CSSProperties}
		>
			<ItemContent>
				{coupon.rule.image?.value && (
					<ItemMedia>
						<img src={coupon.rule.image.value} alt={__('Coupon image', 'leat-crm')} />
					</ItemMedia>
				)}

				<ItemLabel
					as={LabelTag}
					className={!displayDescription ? 'leat-coupon-card__label--no-description' : ''}
				>
					{displayLabel}
				</ItemLabel>

				{displayDescription && (
					<ItemDescription>{displayDescription}</ItemDescription>
				)}

				{/* Coupon code display using shared component */}
				<ItemMeta variant="coupon-code">
					<CouponCode
						code={coupon.code}
						buttonStyles={buttonInlineStyles}
						onApply={handleApplyCoupon}
						onRemove={handleRemoveCoupon}
						isApplied={isApplied}
						isLoading={isApplying}
					/>
				</ItemMeta>

				{/* Error message displayed below the coupon code */}
				{error && (
					<div className="leat-coupon-card__error" role="alert">
						{/* eslint-disable-next-line react/no-danger */}
						<span dangerouslySetInnerHTML={{ __html: error }} />
					</div>
				)}
			</ItemContent>
		</Item>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize coupons list blocks
 * Hydrates for all enrollment states
 */
export function initCouponsListBlocks(): void {
	// Wait for WordPress data stores to be ready
	if (typeof window.wp === 'undefined' || typeof window.wp.data === 'undefined') {
		setTimeout(initCouponsListBlocks, 100);
		return;
	}

	// Find all coupons list blocks
	const blocks = document.querySelectorAll<HTMLElement>(
		'[data-block-type="coupons-list"]'
	);

	if (blocks.length === 0) {
		return;
	}

	// Hydrate each block
	for (const block of blocks) {
		// Parse the button style attribute if it exists
		let buttonStyle: Attributes['buttonStyle'];
		if (block.dataset.buttonStyle) {
			try {
				buttonStyle = JSON.parse(block.dataset.buttonStyle);
			} catch (e) {
				console.error('Failed to parse button style attribute:', e);
			}
		}

		const attributes: Attributes = {
			columns: parseInt(block.dataset.columns || '3', 10),
			cardLabelLevel: parseInt(block.dataset.cardLabelLevel || '4', 10),
			buttonTextSignIn: block.dataset.buttonTextSignIn || 'Sign In',
			buttonTextViewDetails: block.dataset.buttonTextViewDetails || 'View Details',
			messageGuest: block.dataset.messageGuest || 'Please sign in to view your coupons',
			messageNotEnrolled:
				block.dataset.messageNotEnrolled ||
				'Join our program to earn exclusive coupons',
			emptyStateMessage:
				block.dataset.emptyStateMessage || "You don't have any coupons yet",
			emptyStateButtonTextGuest:
				block.dataset.emptyStateButtonTextGuest || 'Sign In',
			emptyStateButtonTextNotEnrolled:
				block.dataset.emptyStateButtonTextNotEnrolled || 'Join Program',
			showEnrollmentPrompt: block.dataset.showEnrollmentPrompt !== 'false',
			imageHeight: parseInt(block.dataset.imageHeight || '200', 10),
			imageFit: (block.dataset.imageFit as 'contain' | 'cover' | 'fill') || 'cover',
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
			buttonStyle: buttonStyle || undefined,
		};

		// Create React root and render
		const root = createRoot(block);
		root.render(<CouponsListView attributes={attributes} />);
	}
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCouponsListBlocks);
} else {
	initCouponsListBlocks();
}
